import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileText, AlertCircle, Download, X, RefreshCw, TrendingUp, TrendingDown, FileDown, Filter, RotateCcw } from 'lucide-react';
import { getSampleDataSet } from './sampleDataUtils';

// ========================== UTILITY FUNCTIONS ==========================
const validateJsonStructure = (jsonData) => {
  const requiredFields = ['Name', 'Total Cost', 'Groups'];
  const missingFields = requiredFields.filter(field => !jsonData.hasOwnProperty(field));
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }

  if (!jsonData['Total Cost'].monthly || !jsonData['Total Cost']['12 months']) {
    throw new Error('Total Cost must contain monthly and 12 months fields');
  }

  if (!jsonData.Groups || Object.keys(jsonData.Groups).length === 0) {
    throw new Error('Groups must contain at least one group');
  }

  Object.entries(jsonData.Groups).forEach(([groupName, group]) => {
    if (!group.Services || !Array.isArray(group.Services)) {
      throw new Error(`Group "${groupName}" must have a Services array`);
    }
  });

  return true;
};

const calculateGroupTotal = (services) => {
  const monthlyTotal = services.reduce((sum, service) => {
    return sum + parseFloat(service['Service Cost']?.monthly || 0);
  }, 0);
  
  const yearlyTotal = services.reduce((sum, service) => {
    return sum + parseFloat(service['Service Cost']?.['12 months'] || 0);
  }, 0);

  return { monthly: monthlyTotal, yearly: yearlyTotal };
};

const calculateCostDifference = (cost1, cost2) => {
  const diff = parseFloat(cost2) - parseFloat(cost1);
  const percentage = parseFloat(cost1) > 0 ? ((diff / parseFloat(cost1)) * 100) : 0;
  return { difference: diff, percentage: percentage };
};

const getGroupColor = (colorClass) => {
  switch(colorClass) {
    case 'production': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    case 'dev-test': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    case 'staging': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    case 'backup': return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
    case 'monitoring': return 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)';
    default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  }
};

// ========================== COMPARISON MODE SELECTOR COMPONENT ==========================
const ComparisonModeSelector = ({ threeWayComparisonMode, onModeChange }) => {
  if (!threeWayComparisonMode) return null;

  return (
    <div style={{
      background: "white",
      borderRadius: "10px",
      padding: "20px",
      marginBottom: "20px",
      boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
      border: "1px solid #e9ecef"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "15px"
      }}>
        <Filter size={20} style={{ marginRight: "10px", color: "#6c757d" }} />
        <h4 style={{ 
          margin: "0", 
          color: "#2c3e50", 
          fontSize: "1.1em", 
          fontWeight: "500" 
        }}>
          Comparison Mode
        </h4>
      </div>
      
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "15px"
      }}>
        <button
          onClick={() => onModeChange('standard')}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "15px",
            borderRadius: "8px",
            border: threeWayComparisonMode === 'standard' ? "2px solid #3b82f6" : "2px solid #e9ecef",
            background: threeWayComparisonMode === 'standard' ? "#f0f9ff" : "white",
            color: threeWayComparisonMode === 'standard' ? "#1976d2" : "#6c757d",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontSize: "0.95em",
            fontWeight: "500"
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "5px", fontWeight: "600" }}>Standard Comparison</div>
            <div style={{ fontSize: "0.85em", opacity: "0.8" }}>A vs B, B vs C, A vs C</div>
          </div>
        </button>

        <button
          onClick={() => onModeChange('baseline')}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "15px",
            borderRadius: "8px",
            border: threeWayComparisonMode === 'baseline' ? "2px solid #10b981" : "2px solid #e9ecef",
            background: threeWayComparisonMode === 'baseline' ? "#ecfdf5" : "white",
            color: threeWayComparisonMode === 'baseline' ? "#059669" : "#6c757d",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontSize: "0.95em",
            fontWeight: "500"
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "5px", fontWeight: "600" }}>Baseline Mode</div>
            <div style={{ fontSize: "0.85em", opacity: "0.8" }}>A vs B, C vs B (B as original)</div>
          </div>
        </button>
      </div>

      <div style={{
        marginTop: "15px",
        padding: "12px",
        background: "#f8f9fa",
        borderRadius: "6px",
        fontSize: "0.85em",
        color: "#6c757d"
      }}>
        <div style={{ fontWeight: "500", marginBottom: "5px" }}>
          {threeWayComparisonMode === 'baseline' ? '📊 Baseline Mode:' : '🔄 Standard Mode:'}
        </div>
        {threeWayComparisonMode === 'baseline' ? 
          'Configuration B is the original price. Shows how much A reduces and C increases compared to B.' :
          'Shows all possible comparisons between the three configurations.'
        }
      </div>
    </div>
  );
};

// ========================== SERVICE CARD COMPONENT ==========================
const ServiceCard = ({ service, compareService = null, isBaselineMode = false, isCurrentBaseline = false }) => {
  const monthlyText = service['Service Cost']?.monthly ? 
    `${parseFloat(service['Service Cost'].monthly).toLocaleString()}` : 'N/A';
  const yearlyText = service['Service Cost']?.['12 months'] ? 
    `${parseFloat(service['Service Cost']['12 months']).toLocaleString()}` : 'N/A';

  // Calculate difference if comparing
  let costDiff = null;
  let hasChanges = false;
  if (compareService && compareService['Service Cost']?.monthly) {
    if (isBaselineMode && !isCurrentBaseline) {
      // In baseline mode, compare current service TO the baseline (compareService)
      // service is A or C, compareService is B
      const currentCost = parseFloat(service['Service Cost']?.monthly || 0);
      const baselineCost = parseFloat(compareService['Service Cost']?.monthly || 0);
      const diff = currentCost - baselineCost;
      const percentage = baselineCost > 0 ? ((diff / baselineCost) * 100) : 0;
      costDiff = { difference: diff, percentage: percentage };
    } else {
      // Standard comparison logic
      costDiff = calculateCostDifference(
        service['Service Cost']?.monthly || 0,
        compareService['Service Cost']?.monthly || 0
      );
    }
    hasChanges = Math.abs(costDiff.difference) > 0.01;
  }

  return (
    <div style={{
      background: hasChanges ? (costDiff.difference > 0 ? 'linear-gradient(to right, #fff5f5, white)' : 'linear-gradient(to right, #f0fff4, white)') : 'white',
      borderRadius: '10px',
      padding: '20px',
      boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      borderLeft: hasChanges ? (costDiff.difference > 0 ? '4px solid #dc3545' : '4px solid #28a745') : 'none'
    }}>
      <div style={{
        fontSize: '1.2em',
        fontWeight: '600',
        color: '#2c3e50',
        marginBottom: '8px',
        borderBottom: '2px solid #e9ecef',
        paddingBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {service['Service Name'] || 'Unknown Service'}
        {hasChanges && (
          <span style={{
            marginLeft: '10px',
            fontSize: '1.2em',
            fontWeight: 'bold',
            color: costDiff.difference > 0 ? '#dc3545' : '#28a745'
          }}>
            {costDiff.difference > 0 ? '↗' : '↘'}
          </span>
        )}
      </div>
      <div style={{
        color: '#6c757d',
        fontSize: '0.9em',
        marginBottom: '15px'
      }}>
        {service.Region || 'No region specified'}
      </div>
      <div style={{
        background: '#f8f9fa',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '15px'
      }}>
        <div style={{
          fontSize: '1.3em',
          fontWeight: 'bold',
          color: '#28a745',
          marginBottom: '5px'
        }}>
          {monthlyText}/month
          {hasChanges && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '0.7em',
              marginTop: '8px',
              padding: '8px 12px',
              borderRadius: '6px',
              fontWeight: '600',
              background: costDiff.difference > 0 ? '#fff5f5' : '#f0fff4',
              color: costDiff.difference > 0 ? '#dc3545' : '#28a745',
              border: costDiff.difference > 0 ? '1px solid #f5c6cb' : '1px solid #c3e6cb'
            }}>
              {costDiff.difference > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {isBaselineMode && !isCurrentBaseline ? 
                `${costDiff.difference > 0 ? '+' : ''}${Math.abs(costDiff.difference).toLocaleString()} vs baseline` :
                `${costDiff.difference > 0 ? '+' : ''}${Math.abs(costDiff.difference).toLocaleString()}`
              }
              ({Math.abs(costDiff.percentage).toFixed(1)}%)
            </div>
          )}
        </div>
        <div style={{ color: '#6c757d', fontSize: '0.95em' }}>
          {yearlyText}/year
        </div>
      </div>
      {service.Properties && Object.keys(service.Properties).length > 0 && (
        <div style={{ fontSize: '0.85em', color: '#495057', textAlign: 'left' }}>
          <ul style={{ listStyle: 'none', padding: '0', margin: '0', textAlign: 'left' }}>
            {Object.entries(service.Properties).map(([key, value], index) => {
              const compareValue = compareService?.Properties?.[key];
              const hasPropertyChange = compareService && compareValue !== undefined && compareValue !== value;
              
              return (
                <li key={index} style={{
                  padding: '3px 0',
                  borderBottom: index === Object.entries(service.Properties).length - 1 ? 'none' : '1px solid #e9ecef',
                  textAlign: 'left',
                  display: 'block',
                  width: '100%',
                  background: hasPropertyChange ? '#fff3cd' : 'transparent',
                  borderLeft: hasPropertyChange ? '3px solid #ffc107' : 'none',
                  paddingLeft: hasPropertyChange ? '8px' : '0',
                  margin: hasPropertyChange ? '2px 0' : '0',
                  borderRadius: hasPropertyChange ? '3px' : '0'
                }}>
                  <strong style={{ fontWeight: '600', color: '#495057' }}>{key}:</strong> {value}
                  {hasPropertyChange && (
                    <span style={{
                      fontSize: '0.8em',
                      color: '#6c757d',
                      fontStyle: 'italic',
                      display: 'block',
                      marginTop: '2px'
                    }}>
                      (baseline: {compareValue})
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// ========================== GROUP SECTION COMPONENT ==========================
const GroupSection = ({ groupName, group, colorClass, compareGroup = null, isBaselineMode = false, isCurrentBaseline = false }) => {
  const total = calculateGroupTotal(group.Services || []);
  const compareTotal = compareGroup ? calculateGroupTotal(compareGroup.Services || []) : null;
  
  let hasGroupDifference = false;
  let groupDiff = null;
  if (compareTotal) {
    if (isBaselineMode && !isCurrentBaseline) {
      // In baseline mode, compare current total TO baseline total
      groupDiff = total.monthly - compareTotal.monthly;
    } else {
      // Standard comparison
      groupDiff = total.monthly - compareTotal.monthly;
    }
    hasGroupDifference = Math.abs(groupDiff) > 0.01;
  }
  
  return (
    <div style={{
      marginBottom: '40px',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        padding: '20px 25px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: getGroupColor(colorClass),
        borderTop: hasGroupDifference ? (groupDiff > 0 ? '4px solid #dc3545' : '4px solid #28a745') : 'none'
      }}>
        <h2 style={{ fontSize: '1.8em', fontWeight: '400', margin: '0' }}>
          {groupName}
          {hasGroupDifference && (
            <span style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.9)' }}>
              {groupDiff > 0 ? ' 📈' : ' 📉'}
            </span>
          )}
        </h2>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold' }}>
            ${total.monthly.toLocaleString()}/month
          </div>
          <div style={{ fontSize: '1em', opacity: '0.9' }}>
            ${total.yearly.toLocaleString()}/year
          </div>
          {hasGroupDifference && (
            <div style={{ marginTop: '10px' }}>
              {(() => {
                const percentage = compareTotal.monthly > 0 ? ((groupDiff / compareTotal.monthly) * 100) : 0;
                return (
                  <div style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    borderRadius: '15px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    marginTop: '8px',
                    background: groupDiff > 0 ? 'rgba(220, 53, 69, 0.15)' : 'rgba(40, 167, 69, 0.15)',
                    color: groupDiff > 0 ? '#dc3545' : '#28a745',
                    border: groupDiff > 0 ? '1px solid rgba(220, 53, 69, 0.3)' : '1px solid rgba(40, 167, 69, 0.3)'
                  }}>
                    {isBaselineMode && !isCurrentBaseline ? 
                      `${groupDiff > 0 ? '+' : ''}${Math.abs(groupDiff).toLocaleString()} vs baseline` :
                      `${groupDiff > 0 ? '+' : ''}${Math.abs(groupDiff).toLocaleString()}`
                    }
                    ({Math.abs(percentage).toFixed(1)}%)
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '20px',
        padding: '25px',
        background: '#f8f9fa'
      }}>
        {(group.Services || []).map((service, index) => {
          let compareService = null;
          if (compareGroup?.Services) {
            const servicesByIndex = compareGroup.Services[index];
            if (servicesByIndex && servicesByIndex['Service Name'] === service['Service Name']) {
              compareService = servicesByIndex;
            } else {
              const matchingServices = compareGroup.Services.filter(s => 
                s['Service Name'] === service['Service Name']
              );
              
              if (matchingServices.length === 1) {
                compareService = matchingServices[0];
              } else if (matchingServices.length > 1) {
                compareService = matchingServices.find(s => 
                  s.Region === service.Region && 
                  JSON.stringify(s.Properties) === JSON.stringify(service.Properties)
                ) || matchingServices[0];
              }
            }
          }
          
          return (
            <ServiceCard 
              key={`${service['Service Name']}-${index}`} 
              service={service} 
              compareService={compareService}
              isBaselineMode={isBaselineMode}
              isCurrentBaseline={isCurrentBaseline}
            />
          );
        })}
      </div>
    </div>
  );
};

// ========================== UPLOAD INTERFACE COMPONENT ==========================
const UploadInterface = ({ 
  data1, data2, data3, 
  isDragging1, isDragging2, isDragging3,
  uploadingForComparison, uploadingForThreeWay,
  error, 
  handleDrop, handleDragOver, handleDragLeave, handleFileInput,
  loadSampleData, resetAll 
}) => {
  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      minHeight: "100vh",
      padding: "20px"
    }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{
          background: "white",
          borderRadius: "15px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #232526 0%, #414345 100%)",
            color: "white",
            padding: "30px",
            textAlign: "center"
          }}>
            <h1 style={{ fontSize: "2.5em", marginBottom: "10px", fontWeight: "300" }}>
              AWS Cost Estimation Dashboard
            </h1>
            <p style={{ fontSize: "1.1em", opacity: "0.8" }}>
              {uploadingForThreeWay && data1 && data2 ? 
                'Two files uploaded! Now upload the third file for three-way comparison.' :
                uploadingForThreeWay && data1 ? 
                'First file uploaded! Upload two more files for three-way comparison.' :
                uploadingForComparison && data1 ? 
                'First file uploaded! Now upload the second file for comparison.' :
                'Upload AWS estimation JSON files for single view, two-way, or three-way comparison'
              }
            </p>
          </div>
          
          <div style={{ padding: "30px" }}>
            {error && (
              <div style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                color: "#dc2626"
              }}>
                <AlertCircle size={20} style={{ marginRight: "12px" }} />
                <span>{error}</span>
              </div>
            )}
            
            <div style={{ display: "grid", gap: "40px", marginBottom: "40px" }}>
              {/* Single File Analysis */}
              <div>
                <h3 style={{ fontSize: "1.4em", marginBottom: "20px", textAlign: "center", color: "#2c3e50" }}>
                  Single File Analysis
                </h3>
                <div
                  style={{
                    border: `2px dashed ${isDragging1 ? '#3b82f6' : '#d1d5db'}`,
                    borderRadius: "8px",
                    padding: "48px",
                    textAlign: "center",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    background: isDragging1 ? '#eff6ff' : 'transparent'
                  }}
                  onDrop={(e) => handleDrop(e, 1, false, false)}
                  onDragOver={(e) => handleDragOver(e, 1)}
                  onDragLeave={(e) => handleDragLeave(e, 1)}
                >
                  <Upload size={40} style={{ color: "#9ca3af", margin: "0 auto 16px auto" }} />
                  <h4 style={{ fontSize: "1.25rem", fontWeight: "500", color: "#374151", margin: "0 0 8px 0" }}>
                    Upload JSON File
                  </h4>
                  <p style={{ color: "#6b7280", margin: "0 0 24px 0" }}>
                    Drag and drop or click to browse
                  </p>
                  
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={(e) => handleFileInput(e, 1, false, false)}
                    style={{ display: "none" }}
                    id="file-input-1"
                  />
                  <label htmlFor="file-input-1" style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "12px 24px",
                    background: "#3b82f6",
                    color: "white",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    textDecoration: "none",
                    border: "none",
                    fontSize: "1rem"
                  }}>
                    <FileText size={18} style={{ marginRight: "8px" }} />
                    Choose File
                  </label>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0" }}>
                <span style={{
                  background: "white",
                  padding: "10px 20px",
                  borderRadius: "20px",
                  fontWeight: "bold",
                  color: "#6c757d",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}>OR</span>
              </div>

              {/* Two-Way Comparison */}
              <div>
                <h3 style={{ fontSize: "1.4em", marginBottom: "20px", textAlign: "center", color: "#2c3e50" }}>
                  Two-Way Comparison
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "30px" }}>
                  <div>
                    <h4 style={{ textAlign: "center", marginBottom: "15px", color: "#495057", fontSize: "1.1em" }}>
                      Configuration A
                    </h4>
                    <div
                      style={{
                        border: `2px dashed ${isDragging1 ? '#3b82f6' : (data1 ? '#28a745' : '#d1d5db')}`,
                        borderRadius: "8px",
                        padding: "24px",
                        textAlign: "center",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        background: isDragging1 ? '#eff6ff' : (data1 ? '#f8fff9' : 'transparent')
                      }}
                      onDrop={(e) => handleDrop(e, 1, true, false)}
                      onDragOver={(e) => handleDragOver(e, 1)}
                      onDragLeave={(e) => handleDragLeave(e, 1)}
                    >
                      {data1 ? (
                        <>
                          <FileText size={32} style={{ color: "#28a745" }} />
                          <p style={{ color: "#28a745", fontWeight: "500" }}>
                            ✅ {data1.Name || 'File 1'} uploaded
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload size={32} style={{ color: "#9ca3af" }} />
                          <p style={{ color: "#6b7280", margin: "0 0 24px 0" }}>First JSON</p>
                          <input
                            type="file"
                            accept=".json,application/json"
                            onChange={(e) => handleFileInput(e, 1, true, false)}
                            style={{ display: "none" }}
                            id="file-input-comp-1"
                          />
                          <label htmlFor="file-input-comp-1" style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "8px 16px",
                            background: "#3b82f6",
                            color: "white",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                            textDecoration: "none",
                            border: "none",
                            fontSize: "0.9rem"
                          }}>
                            Choose
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 style={{ textAlign: "center", marginBottom: "15px", color: "#495057", fontSize: "1.1em" }}>
                      Configuration B
                    </h4>
                    <div
                      style={{
                        border: `2px dashed ${isDragging2 ? '#3b82f6' : (data2 ? '#28a745' : '#d1d5db')}`,
                        borderRadius: "8px",
                        padding: "24px",
                        textAlign: "center",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        background: isDragging2 ? '#eff6ff' : (data2 ? '#f8fff9' : 'transparent')
                      }}
                      onDrop={(e) => handleDrop(e, 2, true, false)}
                      onDragOver={(e) => handleDragOver(e, 2)}
                      onDragLeave={(e) => handleDragLeave(e, 2)}
                    >
                      {data2 ? (
                        <>
                          <FileText size={32} style={{ color: "#28a745" }} />
                          <p style={{ color: "#28a745", fontWeight: "500" }}>
                            ✅ {data2.Name || 'File 2'} uploaded
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload size={32} style={{ color: "#9ca3af" }} />
                          <p style={{ color: "#6b7280", margin: "0 0 24px 0" }}>Second JSON</p>
                          <input
                            type="file"
                            accept=".json,application/json"
                            onChange={(e) => handleFileInput(e, 2, true, false)}
                            style={{ display: "none" }}
                            id="file-input-comp-2"
                          />
                          <label htmlFor="file-input-comp-2" style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "8px 16px",
                            background: "#3b82f6",
                            color: "white",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                            textDecoration: "none",
                            border: "none",
                            fontSize: "0.9rem"
                          }}>
                            Choose
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0" }}>
                <span style={{
                  background: "white",
                  padding: "10px 20px",
                  borderRadius: "20px",
                  fontWeight: "bold",
                  color: "#6c757d",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}>OR</span>
              </div>

              {/* Three-Way Comparison */}
              <div>
                <h3 style={{ fontSize: "1.4em", marginBottom: "20px", textAlign: "center", color: "#2c3e50" }}>
                  Three-Way Comparison
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>
                  {[1, 2, 3].map((fileNum) => (
                    <div key={fileNum}>
                      <h4 style={{ textAlign: "center", marginBottom: "15px", color: "#495057", fontSize: "1.1em" }}>
                        Configuration {String.fromCharCode(64 + fileNum)}
                      </h4>
                      <div
                        style={{
                          border: `2px dashed ${
                            (fileNum === 1 && isDragging1) || (fileNum === 2 && isDragging2) || (fileNum === 3 && isDragging3) 
                              ? '#3b82f6' 
                              : ((fileNum === 1 && data1) || (fileNum === 2 && data2) || (fileNum === 3 && data3)) 
                                ? '#28a745' 
                                : '#d1d5db'
                          }`,
                          borderRadius: "8px",
                          padding: "24px",
                          textAlign: "center",
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                          background: 
                            (fileNum === 1 && isDragging1) || (fileNum === 2 && isDragging2) || (fileNum === 3 && isDragging3)
                              ? '#eff6ff' 
                              : ((fileNum === 1 && data1) || (fileNum === 2 && data2) || (fileNum === 3 && data3))
                                ? '#f8fff9' 
                                : 'transparent'
                        }}
                        onDrop={(e) => handleDrop(e, fileNum, false, true)}
                        onDragOver={(e) => handleDragOver(e, fileNum)}
                        onDragLeave={(e) => handleDragLeave(e, fileNum)}
                      >
                        {((fileNum === 1 && data1) || (fileNum === 2 && data2) || (fileNum === 3 && data3)) ? (
                          <>
                            <FileText size={32} style={{ color: "#28a745" }} />
                            <p style={{ color: "#28a745", fontWeight: "500" }}>
                              ✅ {(fileNum === 1 ? data1?.Name : fileNum === 2 ? data2?.Name : data3?.Name) || `File ${fileNum}`} uploaded
                            </p>
                          </>
                        ) : (
                          <>
                            <Upload size={32} style={{ color: "#9ca3af" }} />
                            <p style={{ color: "#6b7280", margin: "0 0 24px 0" }}>
                              {fileNum === 1 ? 'First' : fileNum === 2 ? 'Second' : 'Third'} JSON
                            </p>
                            <input
                              type="file"
                              accept=".json,application/json"
                              onChange={(e) => handleFileInput(e, fileNum, false, true)}
                              style={{ display: "none" }}
                              id={`file-input-three-${fileNum}`}
                            />
                            <label htmlFor={`file-input-three-${fileNum}`} style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "8px 16px",
                              background: "#3b82f6",
                              color: "white",
                              borderRadius: "8px",
                              cursor: "pointer",
                              transition: "background-color 0.2s ease",
                              textDecoration: "none",
                              border: "none",
                              fontSize: "0.9rem"
                            }}>
                              Choose
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Progress and Sample Data */}
            {(uploadingForComparison || uploadingForThreeWay) && (
              <div style={{
                background: "#e3f2fd",
                border: "1px solid #2196f3",
                borderRadius: "8px",
                padding: "20px",
                margin: "20px 0",
                textAlign: "center"
              }}>
                <div>
                  {uploadingForThreeWay ? (
                    <>
                      <p style={{ margin: "5px 0", color: "#1976d2" }}>
                        ✅ First file uploaded: <strong>{data1?.Name || 'Configuration A'}</strong>
                      </p>
                      {data2 ? (
                        <>
                          <p style={{ margin: "5px 0", color: "#1976d2" }}>
                            ✅ Second file uploaded: <strong>{data2?.Name || 'Configuration B'}</strong>
                          </p>
                          <p style={{ margin: "5px 0", color: "#1976d2" }}>
                            ⏳ Please upload the third file to start three-way comparison
                          </p>
                        </>
                      ) : (
                        <p style={{ margin: "5px 0", color: "#1976d2" }}>
                          ⏳ Please upload the second and third files for three-way comparison
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p style={{ margin: "5px 0", color: "#1976d2" }}>
                        ✅ First file uploaded: <strong>{data1?.Name || 'Configuration A'}</strong>
                      </p>
                      <p style={{ margin: "5px 0", color: "#1976d2" }}>
                        ⏳ Please upload the second file to start comparison
                      </p>
                    </>
                  )}
                </div>
                <button onClick={resetAll} style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  background: "#ff5722",
                  color: "white",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  border: "none",
                  fontSize: "0.9rem",
                  marginTop: "10px"
                }}>
                  <X size={20} style={{ marginRight: "6px" }} />
                  Cancel Upload
                </button>
              </div>
            )}

            {!uploadingForComparison && !uploadingForThreeWay && (
              <div style={{ marginTop: "32px", textAlign: "center" }}>
                <p style={{ color: "#6b7280", marginBottom: "16px" }}>
                  Don't have files? Try our sample comparisons:
                </p>
                <div style={{ display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={() => loadSampleData('two')} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "12px 24px",
                    background: "#10b981",
                    color: "white",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    border: "none",
                    fontSize: "1rem"
                  }}>
                    <Download size={20} style={{ marginRight: "8px" }} />
                    Load Two-Way Sample
                  </button>
                  <button onClick={() => loadSampleData('three')} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "12px 24px",
                    background: "#8b5cf6",
                    color: "white",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    border: "none",
                    fontSize: "1rem"
                  }}>
                    <Download size={20} style={{ marginRight: "8px" }} />
                    Load Three-Way Sample
                  </button>
                </div>
              </div>
            )}
            
            <div style={{
              marginTop: "32px",
              background: "#fefce8",
              border: "1px solid #fde047",
              borderRadius: "8px",
              padding: "16px"
            }}>
              <h4 style={{ fontWeight: "500", color: "#a16207", margin: "0 0 8px 0" }}>
                Expected JSON Format:
              </h4>
              <pre style={{
                fontSize: "0.875rem",
                color: "#a16207",
                background: "#fef3c7",
                padding: "12px",
                borderRadius: "4px",
                overflow: "auto",
                margin: "0"
              }}>{`{
  "Name": "Project Name",
  "Total Cost": {
    "monthly": "1000.00",
    "12 months": "12000.00"
  },
  "Groups": {
    "Production": {
      "Services": [...]
    }
  }
}`}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================== SINGLE FILE VIEW COMPONENT ==========================
const SingleFileView = ({ data, handleFileInput, exportToPDF, resetAll, isExporting }) => {
  const groupColors = ['production', 'dev-test', 'staging', 'backup', 'monitoring'];

  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      minHeight: "100vh",
      padding: "20px"
    }}>
      <div className="dashboard-wrapper-full" style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{
          background: "white",
          borderRadius: "15px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          <div style={{
            background: "linear-gradient(135deg, #232526 0%, #414345 100%)",
            color: "white",
            padding: "30px",
            textAlign: "center"
          }}>
            <h1 style={{ fontSize: "2.5em", marginBottom: "10px", fontWeight: "300" }}>
              AWS Infrastructure Cost Estimation
            </h1>
            <div style={{ fontSize: "1.1em", opacity: "0.8" }}>
              {data.Name || 'Unnamed Project'} - Asia Pacific Region
            </div>
          </div>
          
          <div style={{
            background: "linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)",
            color: "white",
            padding: "25px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            textAlign: "center"
          }}>
            <div style={{ textAlign: "center" }}>
              <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                ${parseFloat(data['Total Cost']?.monthly || 0).toLocaleString()}
              </h3>
              <p style={{ fontSize: "1em", opacity: "0.9" }}>Monthly Total</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                ${parseFloat(data['Total Cost']?.['12 months'] || 0).toLocaleString()}
              </h3>
              <p style={{ fontSize: "1em", opacity: "0.9" }}>12 Months Total</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                {data.Metadata?.Currency || 'USD'}
              </h3>
              <p style={{ fontSize: "1em", opacity: "0.9" }}>Currency</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                {data.Metadata?.['Created On'] || 'N/A'}
              </h3>
              <p style={{ fontSize: "1em", opacity: "0.9" }}>Created On</p>
            </div>
          </div>
          
          <div style={{ padding: "30px" }}>
            {data.Metadata?.['Legal Disclaimer'] && (
              <div style={{
                background: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "5px",
                padding: "15px",
                margin: "20px 0",
                color: "#856404"
              }}>
                <strong>Legal Disclaimer:</strong> {data.Metadata['Legal Disclaimer']}
              </div>
            )}
            
            <div style={{
              marginBottom: "32px",
              textAlign: "center",
              display: "flex",
              gap: "15px",
              justifyContent: "center",
              flexWrap: "wrap"
            }}>
              <label htmlFor="new-file-input" style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 24px",
                background: "#3b82f6",
                color: "white",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                textDecoration: "none"
              }}>
                <Upload size={20} style={{ marginRight: "8px" }} />
                Upload Another File
              </label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => handleFileInput(e, 2, true)}
                style={{ display: "none" }}
                id="new-file-input"
              />
              <button 
                onClick={exportToPDF} 
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "12px 24px",
                  background: "#28a745",
                  color: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  border: "none",
                  fontSize: "1rem",
                  opacity: isExporting ? 0.6 : 1
                }}
                disabled={isExporting}
              >
                <FileDown size={20} style={{ marginRight: "8px" }} />
                {isExporting ? 'Exporting...' : 'Export to PDF'}
              </button>
              <button onClick={resetAll} style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 24px",
                background: "#6c757d",
                color: "white",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                border: "none",
                fontSize: "1rem"
              }}>
                <RefreshCw size={20} style={{ marginRight: "8px" }} />
                Start Over
              </button>
            </div>
            
            {Object.entries(data.Groups || {}).map(([groupName, group], index) => {
              let displayName = groupName;
              if (groupName === 'Production') {
                displayName = 'Production Environment';
              } else if (groupName === 'Dev + 4 Test') {
                displayName = 'Development + 4 Test Environments';
              }
              
              return (
                <GroupSection
                  key={groupName}
                  groupName={displayName}
                  group={group}
                  colorClass={groupColors[index % groupColors.length]}
                />
              );
            })}
          </div>
          
          <div style={{
            background: "#343a40",
            color: "white",
            padding: "20px",
            textAlign: "center",
            fontSize: "0.9em"
          }}>
            <p style={{ margin: "0" }}>Generated from AWS Pricing Calculator</p>
            {data.Metadata?.['Share Url'] && (
              <p style={{ margin: "0" }}>
                Share URL: <a 
                  href={data.Metadata['Share Url']} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#ffc107" }}
                >
                  View Original
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================== COST SUMMARY COMPONENT ==========================
const CostSummary = ({ data1, data2, data3 = null, mode = 'two', threeWayComparisonMode = 'standard' }) => {
  if (mode === 'three' && data1 && data2 && data3) {
    let comparisons = [];
    
    if (threeWayComparisonMode === 'baseline') {
      // B as baseline/original: A vs B (A cost - B cost), C vs B (C cost - B cost)
      const monthlyA = parseFloat(data1['Total Cost']?.monthly || 0);
      const monthlyB = parseFloat(data2['Total Cost']?.monthly || 0);
      const monthlyC = parseFloat(data3['Total Cost']?.monthly || 0);
      
      // A vs B: How much A differs from B (should be negative = savings)
      const diffAB = monthlyA - monthlyB;
      const percentAB = monthlyB > 0 ? ((diffAB / monthlyB) * 100) : 0;
      
      // C vs B: How much C differs from B (should be positive = increase)
      const diffCB = monthlyC - monthlyB;
      const percentCB = monthlyB > 0 ? ((diffCB / monthlyB) * 100) : 0;
      
      comparisons = [
        { 
          label: 'A vs B', 
          difference: diffAB, 
          percentage: percentAB,
          configName: 'A',
          costA: monthlyA,
          costB: monthlyB
        },
        { 
          label: 'C vs B', 
          difference: diffCB, 
          percentage: percentCB,
          configName: 'C',
          costC: monthlyC,
          costB: monthlyB
        }
      ];
    } else {
      // Standard mode: A vs B, B vs C, A vs C
      comparisons = [
        { label: 'A vs B', cost1: data1, cost2: data2, description: 'A compared to B' },
        { label: 'B vs C', cost1: data2, cost2: data3, description: 'B compared to C' },
        { label: 'A vs C', cost1: data1, cost2: data3, description: 'A compared to C' }
      ];
    }

    return (
      <div style={{
        background: "white",
        borderRadius: "15px",
        padding: "40px",
        boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
        textAlign: "center"
      }}>
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{
            fontSize: "1.8em",
            marginBottom: "10px",
            fontWeight: "400",
            color: "#2c3e50"
          }}>
            {threeWayComparisonMode === 'baseline' ? 'Baseline Cost Analysis' : 'Three-Way Cost Analysis'}
          </h3>
          {threeWayComparisonMode === 'baseline' && (
            <p style={{ color: "#6c757d", fontSize: "1em", margin: "5px 0" }}>
              Configuration B as original price reference
            </p>
          )}
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${comparisons.length}, 1fr)`,
          gap: "20px"
        }}>
          {comparisons.map((comparison, index) => {
            let difference, percentage, label, actionText, description;
            
            if (threeWayComparisonMode === 'baseline') {
              // Use pre-calculated baseline comparisons
              difference = comparison.difference;
              percentage = comparison.percentage;
              label = comparison.label;
              
              if (difference < 0) {
                actionText = `${Math.abs(difference).toLocaleString()} reduction`;
              } else if (difference > 0) {
                actionText = `${difference.toLocaleString()} increase`;
              } else {
                actionText = 'No change';
              }
              description = `Config ${comparison.configName} vs Original B`;
            } else {
              // Standard mode calculation
              const monthly1 = parseFloat(comparison.cost1['Total Cost']?.monthly || 0);
              const monthly2 = parseFloat(comparison.cost2['Total Cost']?.monthly || 0);
              difference = monthly2 - monthly1;
              percentage = monthly1 > 0 ? ((difference / monthly1) * 100) : 0;
              label = comparison.label;
              actionText = difference > 0 ? ' increase' : ' savings';
              description = comparison.description;
            }
            
            return (
              <div key={label} style={{
                padding: "30px",
                borderRadius: "15px",
                color: "white",
                background: difference > 0 ? 
                  "linear-gradient(135deg, #dc3545 0%, #e55a6a 100%)" : 
                  "linear-gradient(135deg, #28a745 0%, #34ce57 100%)",
                border: difference > 0 ? "2px solid #b21e2f" : "2px solid #1e7e34"
              }}>
                <h4 style={{ fontSize: "1.2em", marginBottom: "10px", fontWeight: "500" }}>{label}</h4>
                <div style={{ fontSize: "2.2em", fontWeight: "bold", marginBottom: "10px" }}>
                  {threeWayComparisonMode === 'baseline' ? 
                    `${Math.abs(difference).toLocaleString()}/month` :
                    `${difference > 0 ? '+' : ''}${Math.abs(difference).toLocaleString()}/month`
                  }
                </div>
                <div style={{ fontSize: "1.2em", opacity: "0.9" }}>
                  {threeWayComparisonMode === 'baseline' ? 
                    `${Math.abs(percentage).toFixed(1)}% ${difference < 0 ? 'savings' : 'increase'}` :
                    `${difference > 0 ? '+' : ''}${percentage.toFixed(1)}%${actionText}`
                  }
                </div>
                <div style={{ fontSize: "0.9em", opacity: "0.8", marginTop: "10px" }}>
                  {description}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Two-way comparison
  const monthly1 = parseFloat(data1['Total Cost']?.monthly || 0);
  const monthly2 = parseFloat(data2['Total Cost']?.monthly || 0);
  const difference = monthly2 - monthly1;
  const percentage = monthly1 > 0 ? ((difference / monthly1) * 100) : 0;
  
  if (Math.abs(difference) <= 0.01) {
    return (
      <div style={{
        background: "white",
        borderRadius: "15px",
        padding: "30px",
        boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
        textAlign: "center"
      }}>
        <div style={{
          display: "inline-block",
          padding: "30px",
          borderRadius: "15px",
          color: "white",
          background: "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)"
        }}>
          <h3 style={{ fontSize: "1.5em", marginBottom: "15px", fontWeight: "400" }}>
            Cost Analysis
          </h3>
          <div style={{ fontSize: "2.2em", fontWeight: "bold", marginBottom: "10px" }}>
            No Significant Change
          </div>
          <div style={{ fontSize: "1.2em", opacity: "0.9" }}>
            Costs are essentially identical
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      background: "white",
      borderRadius: "15px",
      padding: "30px",
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      textAlign: "center"
    }}>
      <div style={{
        display: "inline-block",
        padding: "30px",
        borderRadius: "15px",
        color: "white",
        background: difference > 0 ? 
          "linear-gradient(135deg, #dc3545 0%, #e55a6a 100%)" : 
          "linear-gradient(135deg, #28a745 0%, #34ce57 100%)",
        border: difference > 0 ? "2px solid #b21e2f" : "2px solid #1e7e34"
      }}>
        <h3 style={{ fontSize: "1.5em", marginBottom: "15px", fontWeight: "400" }}>
          Cost Difference
        </h3>
        <div style={{ fontSize: "2.2em", fontWeight: "bold", marginBottom: "10px" }}>
          {difference > 0 ? '+' : ''}${Math.abs(difference).toLocaleString()}/month
        </div>
        <div style={{ fontSize: "1.2em", opacity: "0.9" }}>
          {difference > 0 ? '+' : ''}{percentage.toFixed(1)}% 
          {difference > 0 ? ' increase' : ' savings'}
        </div>
      </div>
    </div>
  );
};

// ========================== COMPARISON VIEW COMPONENT ==========================
const ComparisonView = ({ 
  data1, data2, data3 = null, mode = 'two', 
  threeWayComparisonMode = 'standard', onThreeWayModeChange,
  exportToPDF, resetAll, isExporting 
}) => {
  const groupColors = ['production', 'dev-test', 'staging', 'backup', 'monitoring'];
  const isThreeWay = mode === 'three' && data3;

  const renderConfigurationPanel = (data, title, gradient, compareData = null, isCurrentBaseline = false) => (
    <div style={{ minWidth: "0" }}>
      <div style={{
        background: "white",
        borderRadius: "15px",
        boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }}>
        <div style={{
          background: "linear-gradient(135deg, #232526 0%, #414345 100%)",
          color: "white",
          padding: isThreeWay ? "20px" : "30px",
          textAlign: "center"
        }}>
          <h1 style={{ 
            fontSize: isThreeWay ? "1.8em" : "2.5em", 
            marginBottom: "10px", 
            fontWeight: "300" 
          }}>
            {isThreeWay ? title : "AWS Infrastructure Cost Estimation"}
          </h1>
          <div style={{ fontSize: "1.1em", opacity: "0.8" }}>
            {data.Name || title} - Asia Pacific Region
          </div>
        </div>
        
        <div style={{
          background: gradient,
          color: "white",
          padding: isThreeWay ? "20px" : "25px",
          display: "grid",
          gridTemplateColumns: isThreeWay ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(200px, 1fr))",
          gap: isThreeWay ? "15px" : "20px",
          textAlign: "center"
        }}>
          <div>
            <h3 style={{ fontSize: isThreeWay ? "1.4em" : "1.8em", marginBottom: "5px" }}>
              ${parseFloat(data['Total Cost']?.monthly || 0).toLocaleString()}
            </h3>
            <p style={{ fontSize: isThreeWay ? "0.9em" : "1em", opacity: "0.9" }}>
              {isThreeWay ? "Monthly" : "Monthly Total"}
            </p>
          </div>
          <div>
            <h3 style={{ fontSize: isThreeWay ? "1.4em" : "1.8em", marginBottom: "5px" }}>
              ${parseFloat(data['Total Cost']?.['12 months'] || 0).toLocaleString()}
            </h3>
            <p style={{ fontSize: isThreeWay ? "0.9em" : "1em", opacity: "0.9" }}>
              {isThreeWay ? "12 Months" : "12 Months Total"}
            </p>
          </div>
          {!isThreeWay && (
            <>
              <div>
                <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                  {data.Metadata?.Currency || 'USD'}
                </h3>
                <p style={{ fontSize: "1em", opacity: "0.9" }}>Currency</p>
              </div>
              <div>
                <h3 style={{ fontSize: "1.8em", marginBottom: "5px" }}>
                  {data.Metadata?.['Created On'] || 'N/A'}
                </h3>
                <p style={{ fontSize: "1em", opacity: "0.9" }}>Created On</p>
              </div>
            </>
          )}
        </div>
        
        <div style={{ padding: isThreeWay ? "20px" : "30px" }}>
          {!isThreeWay && data.Metadata?.['Legal Disclaimer'] && (
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "5px",
              padding: "15px",
              margin: "20px 0",
              color: "#856404"
            }}>
              <strong>Legal Disclaimer:</strong> {data.Metadata['Legal Disclaimer']}
            </div>
          )}
          
          {Object.entries(data.Groups || {}).map(([groupName, group], index) => {
            const compareGroup = compareData?.Groups?.[groupName];
            let displayName = groupName;
            if (groupName === 'Production') {
              displayName = 'Production Environment';
            } else if (groupName === 'Dev + 4 Test') {
              displayName = 'Development + 4 Test Environments';
            }
            
            return (
              <GroupSection
                key={groupName}
                groupName={displayName}
                group={group}
                colorClass={groupColors[index % groupColors.length]}
                compareGroup={compareGroup}
                isBaselineMode={isThreeWay && threeWayComparisonMode === 'baseline'}
                isCurrentBaseline={isCurrentBaseline}
              />
            );
          })}
        </div>
        
        {!isThreeWay && (
          <div style={{
            background: "#343a40",
            color: "white",
            padding: "20px",
            textAlign: "center",
            fontSize: "0.9em"
          }}>
            <p style={{ margin: "0" }}>Generated from AWS Pricing Calculator</p>
            {data.Metadata?.['Share Url'] && (
              <p style={{ margin: "0" }}>
                Share URL: <a 
                  href={data.Metadata['Share Url']} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#ffc107" }}
                >
                  View Original
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      minHeight: "100vh",
      padding: "20px"
    }}>
      <div className={isThreeWay ? "dashboard-wrapper-threeway" : "dashboard-wrapper-comparison"} 
           style={{ maxWidth: isThreeWay ? "2400px" : "1800px", margin: "0 auto" }}>
        <div style={{
          background: "white",
          borderRadius: "15px",
          padding: "30px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 5px 15px rgba(0,0,0,0.1)"
        }}>
          <h1 style={{
            fontSize: "2.5em",
            fontWeight: "300",
            color: "#2c3e50",
            margin: "0"
          }}>
            AWS Cost Estimation - {isThreeWay ? 'Three-Way' : ''} Comparison
          </h1>
          <div style={{ display: "flex", gap: "15px" }}>
            <button 
              onClick={exportToPDF} 
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "12px 24px",
                background: "#28a745",
                color: "white",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                border: "none",
                fontSize: "1rem",
                opacity: isExporting ? 0.6 : 1
              }}
              disabled={isExporting}
            >
              <FileDown size={20} style={{ marginRight: "8px" }} />
              {isExporting ? 'Exporting...' : 'Export to PDF'}
            </button>
            <button onClick={resetAll} style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "12px 24px",
              background: "#6c757d",
              color: "white",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              border: "none",
              fontSize: "1rem"
            }}>
              <RefreshCw size={20} style={{ marginRight: "8px" }} />
              Start Over
            </button>
          </div>
        </div>

        {/* Comparison Mode Selector for Three-Way */}
        {isThreeWay && (
          <ComparisonModeSelector 
            threeWayComparisonMode={threeWayComparisonMode}
            onModeChange={onThreeWayModeChange}
          />
        )}

        <div style={{
          display: "grid",
          gridTemplateColumns: isThreeWay ? "1fr auto 1fr auto 1fr" : "1fr auto 1fr",
          gap: isThreeWay ? "15px" : "20px",
          marginBottom: "20px"
        }}>
          {/* Configuration A */}
          {renderConfigurationPanel(
            data1, 
            "Configuration A", 
            "linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)",
            threeWayComparisonMode === 'baseline' ? data2 : data2, // A always compares to B
            false // A is never the baseline
          )}

          {/* VS Badge */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: isThreeWay ? "40px" : "60px"
          }}>
            <div style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              padding: isThreeWay ? "10px 15px" : "15px 20px",
              borderRadius: "50%",
              fontWeight: "bold",
              fontSize: isThreeWay ? "1em" : "1.2em",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)"
            }}>
              VS
            </div>
          </div>

          {/* Configuration B */}
          {renderConfigurationPanel(
            data2, 
            "Configuration B (Original)", 
            isThreeWay ? "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" : "linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)",
            threeWayComparisonMode === 'baseline' ? null : data1, // In baseline mode, B doesn't compare to anyone
            threeWayComparisonMode === 'baseline' // B is the baseline in baseline mode
          )}

          {isThreeWay && (
            <>
              {/* Second VS Badge */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px"
              }}>
                <div style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  padding: "10px 15px",
                  borderRadius: "50%",
                  fontWeight: "bold",
                  fontSize: "1em",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.2)"
                }}>
                  VS
                </div>
              </div>

              {/* Configuration C */}
              {renderConfigurationPanel(
                data3, 
                "Configuration C", 
                "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                threeWayComparisonMode === 'baseline' ? data2 : data2, // C always compares to B
                false // C is never the baseline
              )}
            </>
          )}
        </div>

        {/* Cost Summary */}
        <CostSummary 
          data1={data1} 
          data2={data2} 
          data3={data3} 
          mode={mode} 
          threeWayComparisonMode={threeWayComparisonMode}
        />
      </div>
    </div>
  );
};

// ========================== MAIN DASHBOARD COMPONENT ==========================
const AWSCostEstimationDashboard = () => {
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const [data3, setData3] = useState(null);
  const [error, setError] = useState(null);
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);
  const [isDragging3, setIsDragging3] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [threeWayMode, setThreeWayMode] = useState(false);
  const [threeWayComparisonMode, setThreeWayComparisonMode] = useState('standard'); // 'standard' or 'baseline'
  const [uploadingForComparison, setUploadingForComparison] = useState(false);
  const [uploadingForThreeWay, setUploadingForThreeWay] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Load html2pdf library
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // File handling functions
  const handleFileUpload = useCallback((file, fileNumber = 1, forComparison = false, forThreeWay = false) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        validateJsonStructure(jsonData);
        
        if (fileNumber === 1) {
          setData1(jsonData);
          if (forThreeWay) {
            setUploadingForThreeWay(true);
          } else if (forComparison) {
            setUploadingForComparison(true);
          }
        } else if (fileNumber === 2) {
          setData2(jsonData);
          if (forThreeWay && data1 && !data3) {
            // Continue three-way upload
          } else if (forComparison && data1) {
            setComparisonMode(true);
            setUploadingForComparison(false);
          }
        } else if (fileNumber === 3) {
          setData3(jsonData);
          if (forThreeWay && data1 && data2) {
            setThreeWayMode(true);
            setUploadingForThreeWay(false);
          }
        }
        setError(null);
      } catch (err) {
        setError(`Error parsing JSON: ${err.message}`);
      }
    };
    reader.readAsText(file);
  }, [data1, data2, data3]);

  const handleDrop = useCallback((e, fileNumber = 1, forComparison = false, forThreeWay = false) => {
    e.preventDefault();
    if (fileNumber === 1) setIsDragging1(false);
    else if (fileNumber === 2) setIsDragging2(false);
    else if (fileNumber === 3) setIsDragging3(false);
    
    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file => file.type === 'application/json' || file.name.endsWith('.json'));
    
    if (jsonFile) {
      handleFileUpload(jsonFile, fileNumber, forComparison, forThreeWay);
    } else {
      setError('Please upload a valid JSON file');
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e, fileNumber = 1) => {
    e.preventDefault();
    if (fileNumber === 1) setIsDragging1(true);
    else if (fileNumber === 2) setIsDragging2(true);
    else if (fileNumber === 3) setIsDragging3(true);
  }, []);

  const handleDragLeave = useCallback((e, fileNumber = 1) => {
    e.preventDefault();
    if (fileNumber === 1) setIsDragging1(false);
    else if (fileNumber === 2) setIsDragging2(false);
    else if (fileNumber === 3) setIsDragging3(false);
  }, []);

  const handleFileInput = useCallback((e, fileNumber = 1, forComparison = false, forThreeWay = false) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, fileNumber, forComparison, forThreeWay);
    }
  }, [handleFileUpload]);

  const loadSampleData = (mode = 'two') => {
    setData1(getSampleDataSet(1));
    setData2(getSampleDataSet(2));
    if (mode === 'three') {
      setData3(getSampleDataSet(3));
      setThreeWayMode(true);
      setComparisonMode(false);
    } else {
      setData3(null);
      setComparisonMode(true);
      setThreeWayMode(false);
    }
    setUploadingForComparison(false);
    setUploadingForThreeWay(false);
    setError(null);
  };

  const resetAll = () => {
    setData1(null);
    setData2(null);
    setData3(null);
    setComparisonMode(false);
    setThreeWayMode(false);
    setThreeWayComparisonMode('standard');
    setUploadingForComparison(false);
    setUploadingForThreeWay(false);
    setError(null);
  };

  const handleThreeWayModeChange = (mode) => {
    setThreeWayComparisonMode(mode);
  };

  const exportToPDF = useCallback(() => {
    if (!window.html2pdf) {
      setError('PDF export library not loaded. Please try again in a moment.');
      return;
    }

    setIsExporting(true);
    
    let filename = 'AWS_Cost_Estimation';
    if (threeWayMode && data1 && data2 && data3) {
      const modePrefix = threeWayComparisonMode === 'baseline' ? 'Baseline' : '3Way';
      filename = `AWS_Cost_${modePrefix}_Comparison_${data1.Name || 'A'}_vs_${data2.Name || 'B'}_vs_${data3.Name || 'C'}`;
    } else if (data1 && data2) {
      filename = `AWS_Cost_Comparison_${data1.Name || 'ConfigA'}_vs_${data2.Name || 'ConfigB'}`;
    } else if (data1 || data2 || data3) {
      const data = data1 || data2 || data3;
      filename = `AWS_Cost_Estimation_${data.Name || 'Report'}`;
    }
    filename = filename.replace(/[^a-zA-Z0-9_-]/g, '_') + '.pdf';

    const element = document.querySelector('.dashboard-wrapper-full, .dashboard-wrapper-comparison, .dashboard-wrapper-threeway');
    
    if (!element) {
      setError('Unable to find dashboard content for export.');
      setIsExporting(false);
      return;
    }

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff',
        width: element.scrollWidth, height: element.scrollHeight
      },
      jsPDF: { 
        unit: 'in', 
        format: threeWayMode ? 'a2' : (data1 && data2 ? 'a3' : 'a4'), 
        orientation: (data1 && data2) || threeWayMode ? 'landscape' : 'portrait',
        compress: true
      }
    };

    window.html2pdf().set(opt).from(element).save()
      .then(() => setIsExporting(false))
      .catch((error) => {
        console.error('PDF export failed:', error);
        setError('Failed to export PDF. Please try again.');
        setIsExporting(false);
      });
  }, [data1, data2, data3, threeWayMode, threeWayComparisonMode]);

  // Render logic
  const shouldShowUpload = (!data1 && !data2 && !data3) || 
                          (uploadingForComparison && !data2) || 
                          (uploadingForThreeWay && (!data2 || !data3));

  if (shouldShowUpload) {
    return (
      <UploadInterface
        data1={data1} data2={data2} data3={data3}
        isDragging1={isDragging1} isDragging2={isDragging2} isDragging3={isDragging3}
        uploadingForComparison={uploadingForComparison}
        uploadingForThreeWay={uploadingForThreeWay}
        error={error}
        handleDrop={handleDrop}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleFileInput={handleFileInput}
        loadSampleData={loadSampleData}
        resetAll={resetAll}
      />
    );
  }

  // Single file view
  const isSingleFile = ((data1 && !data2 && !data3) || (!data1 && data2 && !data3) || (!data1 && !data2 && data3)) && 
                      !uploadingForComparison && !uploadingForThreeWay;
  
  if (isSingleFile) {
    const data = data1 || data2 || data3;
    return (
      <SingleFileView
        data={data}
        handleFileInput={handleFileInput}
        exportToPDF={exportToPDF}
        resetAll={resetAll}
        isExporting={isExporting}
      />
    );
  }

  // Three-way comparison view
  if (threeWayMode && data1 && data2 && data3) {
    return (
      <ComparisonView
        data1={data1}
        data2={data2}
        data3={data3}
        mode="three"
        threeWayComparisonMode={threeWayComparisonMode}
        onThreeWayModeChange={handleThreeWayModeChange}
        exportToPDF={exportToPDF}
        resetAll={resetAll}
        isExporting={isExporting}
      />
    );
  }

  // Two-way comparison view
  return (
    <ComparisonView
      data1={data1}
      data2={data2}
      mode="two"
      exportToPDF={exportToPDF}
      resetAll={resetAll}
      isExporting={isExporting}
    />
  );
};

export default AWSCostEstimationDashboard;